import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertCircle, Home, RotateCcw } from 'lucide-react';
import { OWN_TEXT_IDEA_CODE } from '@masha/shared';
import { api, type SessionGetResponse } from '../../lib/api';
import { Button, Spinner, Toast } from '../ui';
import { CarouselResultView } from './CarouselResultView';
import { OwnTextStep } from './OwnTextStep';
import { StyleStep } from './StyleStep';

export interface OwnTextSessionProps {
  data: SessionGetResponse;
  readonly: boolean;
  initialOpenRewrite: boolean;
}

/**
 * Iter 20: the whole lifecycle of an own-text carousel session. One text screen
 * replaces the questionnaire and its summary; style, result and rewrite are the
 * regular carousel components.
 */
export function OwnTextSession({ data, readonly, initialOpenRewrite }: OwnTextSessionProps) {
  const navigate = useNavigate();
  const [view, setView] = useState<'text' | 'style'>('text');
  const [editing, setEditing] = useState(false);
  const { session } = data;

  if (session.status === 'failed') return <OwnTextFailed data={data} />;
  if (session.status === 'generating') return <OwnTextGenerating sessionId={session.id} />;

  if (session.status === 'completed' && data.last_generation && !editing) {
    return (
      <CarouselResultView
        sessionId={session.id}
        rewriteCost={data.rewrite_cost}
        initialOpenRewrite={initialOpenRewrite}
        readonly={readonly}
        onEditAnswers={() => setEditing(true)}
      />
    );
  }

  if (editing) {
    return (
      <OwnTextStep
        session={session}
        mode="rewrite-edit"
        onSaved={() => {
          setEditing(false);
          navigate(`/sessions/${session.id}`, { replace: true, state: { openRewrite: true } });
        }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  if (view === 'style') {
    return (
      <StyleStep
        sessionId={session.id}
        initialStyle={data.style_code}
        initialNick={data.nick}
        backLabel="К тексту"
        onBack={() => setView('text')}
        onGenerated={() => setView('text')}
      />
    );
  }

  return <OwnTextStep session={session} mode="initial" onSaved={() => setView('style')} />;
}

/** Opened mid-generation: poll the session so the screen moves on by itself. */
function OwnTextGenerating({ sessionId }: { sessionId: number }) {
  const qc = useQueryClient();
  useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => api.getSession(sessionId),
    refetchInterval: 5_000,
  });
  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Spinner size="lg" className="mb-4" />
      <h1 className="text-lg font-semibold mb-1">Карусель ещё собирается</h1>
      <p className="text-sm text-tg-hint mb-6">Обычно это занимает 30-60 секунд.</p>
      <Button
        onClick={() => qc.invalidateQueries({ queryKey: ['session', sessionId] })}
        variant="secondary"
        size="lg"
        leftIcon={<RotateCcw size={18} />}
      >
        Обновить
      </Button>
    </div>
  );
}

/**
 * Retry keeps the pasted text: the new session gets the failed session's answers.
 * Create may resume another own-text draft instead; if that one already has text,
 * it is newer work and is kept as is.
 */
function OwnTextFailed({ data }: { data: SessionGetResponse }) {
  const navigate = useNavigate();
  const retry = useMutation({
    mutationFn: async () => {
      const { a_1, a_2, a_3 } = data.session.answers;
      const res = await api.createSession({ idea_code: OWN_TEXT_IDEA_CODE });
      const keepResumed = res.resumed && Boolean(res.session.answers.a_1);
      if (a_1 && a_2 && a_3 && !keepResumed) {
        await api.updateSessionAnswers(res.session.id, { a_1, a_2, a_3 });
      }
      return res.session.id;
    },
    onSuccess: (id) => navigate(`/sessions/${id}`, { replace: true }),
  });

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <div className="w-16 h-16 rounded-2xl bg-destructive-subtle text-destructive flex items-center justify-center mb-4">
        <AlertCircle size={28} />
      </div>
      <h1 className="text-lg font-semibold mb-2">Не удалось собрать карусель</h1>
      <p className="text-sm text-tg-hint mb-6 max-w-xs">
        Что-то пошло не так на стороне сервиса. Кредит возвращён на баланс, текст сохранён.
      </p>
      {retry.error && (
        <Toast tone="destructive" className="mb-3 w-full max-w-xs">
          {retry.error.message}
        </Toast>
      )}
      <Button
        onClick={() => retry.mutate()}
        loading={retry.isPending}
        variant="primary"
        size="lg"
        leftIcon={!retry.isPending ? <RotateCcw size={18} /> : undefined}
        className="w-full max-w-xs mb-2"
      >
        Попробовать снова
      </Button>
      <Button
        onClick={() => navigate('/')}
        variant="secondary"
        size="lg"
        leftIcon={<Home size={18} />}
        className="w-full max-w-xs"
        hapticKind="light"
      >
        На главную
      </Button>
    </div>
  );
}
